import React, { RefObject, useCallback, useRef } from 'react'

import { useTheme } from '@chakra-ui/react'
import { RemoveIcon } from '@verbit-ai/icons-library'

import * as S from './styles'

type SearchFieldProps = {
    value: string
    onChange: (value: string) => unknown
    onSearch: (value: string) => unknown
    onFocus?: () => unknown
    onBlur?: () => unknown
    inputRef: RefObject<HTMLInputElement | null>
    activeIndex: number | null
}

export const SearchField = (props: SearchFieldProps) => {
    const { value, onChange, onSearch, onFocus, onBlur, inputRef } = props
    const didClearRef = useRef(false)
    const {
        palette: {
            search: { clearButtonColor },
        },
    } = useTheme()

    const onTextChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            onChange(e.target.value)
        },
        [onChange],
    )

    const onClearSearch = useCallback(() => {
        onChange('')
        didClearRef.current = true

        setTimeout(() => inputRef.current?.focus())
        setTimeout(() => (didClearRef.current = false), 200)
    }, [inputRef, onChange])

    const onClearSearchEnter = useCallback(
        (e: React.KeyboardEvent<HTMLButtonElement>) => {
            if (e.key === 'Enter') {
                onClearSearch()
            }
        },
        [onClearSearch],
    )

    const onEnter = useCallback(
        (e: React.KeyboardEvent<HTMLInputElement>) => {
            if (e.key === 'Enter' && !didClearRef.current) {
                onSearch(value)
            }
        },
        [onSearch, value],
    )

    const onTextFocus = useCallback(() => {
        if (!didClearRef.current) {
            onFocus?.()
        }
    }, [onFocus])

    const onTextBlur = useCallback(() => {
        onBlur?.()
    }, [onBlur])

    return (
        <S.Wrapper>
            <S.Label htmlFor="search-input">Search Transcript</S.Label>
            <S.TextField
                ref={inputRef}
                value={value}
                type="search"
                id="search-input"
                className="hide-clear"
                autoComplete="off"
                placeholder="Find"
                onChange={onTextChange}
                onKeyUp={onEnter}
                onFocus={onTextFocus}
                onBlur={onTextBlur}
            />
            {!!value.length && (
                <S.ClearButton type="reset" tabIndex={0} aria-label="Clear" onClick={onClearSearch} onKeyDown={onClearSearchEnter}>
                    <RemoveIcon color={clearButtonColor} />
                </S.ClearButton>
            )}
        </S.Wrapper>
    )
}
